import {
  DeploymentCurrentRecord,
  DeploymentCustomer,
  DeploymentRecord,
  DeploymentServer,
  DeploymentTargetRecord,
  RepositoryCatalogRecord,
} from '../../types/deployment';

export const asNamedEntity = <T extends { _id: string; name?: string; slug?: string }>(
  value: string | T | null | undefined
) => {
  if (!value || typeof value === 'string') {
    return null;
  }

  return value;
};

export const formatDate = (value?: string | null) => {
  if (!value) {
    return '-';
  }

  return new Date(value).toLocaleString();
};

export const getDeploymentActivityAt = (deploymentRecord: {
  lastStatusAt?: string | null;
  updatedAt?: string;
  createdAt?: string;
}) => (
  deploymentRecord.lastStatusAt
  || deploymentRecord.updatedAt
  || deploymentRecord.createdAt
  || ''
);

export const getCustomerName = (customer: string | DeploymentCustomer | null | undefined) => (
  asNamedEntity(customer)?.name || '-'
);

export const getServerName = (server: string | DeploymentServer | null | undefined) => (
  asNamedEntity(server)?.name || '-'
);

export const getRepositoryName = (repository: string | RepositoryCatalogRecord | null | undefined) => (
  asNamedEntity(repository)?.pathWithNamespace || asNamedEntity(repository)?.name || '-'
);

export const resolveDeploymentTargetForRecord = (
  deploymentRecord: DeploymentRecord | DeploymentCurrentRecord,
  targets: DeploymentTargetRecord[]
) => {
  const deploymentTarget = deploymentRecord.deploymentTarget;
  if (deploymentTarget && typeof deploymentTarget !== 'string') {
    return deploymentTarget;
  }

  return targets.find((target) => target._id === deploymentTarget) || null;
};

const targetSupportsDockerRestart = (target: DeploymentTargetRecord | null | undefined) => (
  Boolean(
    target
    && (
      target.defaultDeploymentMode === 'docker'
      || target.setupFamily === 'docker'
      || target.allowedDeploymentModes?.includes('docker')
    )
  )
);

const RESTART_ELIGIBLE_HEALTHCHECK_FAILURE_PATTERNS = [
  /\bhealthcheck failed after\b/i,
  /\bservice not healthy\b/i,
];

const isRestartEligibleHealthcheckFailure = (deploymentRecord: DeploymentRecord): boolean => {
  if (deploymentRecord.status !== 'failed') {
    return false;
  }

  const message = String(
    deploymentRecord.setupProgress?.failedStepMessage
    || deploymentRecord.errorMessage
    || ''
  ).trim();

  return RESTART_ELIGIBLE_HEALTHCHECK_FAILURE_PATTERNS.some((pattern) => pattern.test(message));
};

export const canRestartDeploymentRecord = (
  deploymentRecord: DeploymentRecord,
  targets: DeploymentTargetRecord[]
) => {
  if (deploymentRecord.status !== 'success' && !isRestartEligibleHealthcheckFailure(deploymentRecord)) {
    return false;
  }

  const normalizedMode = String(deploymentRecord.deploymentMode || '').trim();
  if (normalizedMode) {
    return normalizedMode === 'docker';
  }

  return targetSupportsDockerRestart(resolveDeploymentTargetForRecord(deploymentRecord, targets));
};

export const statusColor = (status?: string) => {
  switch (status) {
    case 'success':
      return 'success';
    case 'failed':
      return 'error';
    case 'running':
      return 'info';
    case 'manual_job_pending':
      return 'warning';
    default:
      return 'default';
  }
};

export const matchesDeploymentSearch = (deploymentRecord: DeploymentRecord, search: string) => {
  const normalizedSearch = search.trim().toLowerCase();
  if (!normalizedSearch) {
    return true;
  }

  const fields = [
    getCustomerName(deploymentRecord.customer),
    getServerName(deploymentRecord.server),
    getRepositoryName(deploymentRecord.repository),
    deploymentRecord.environment,
    deploymentRecord.actionType,
    deploymentRecord.status,
    deploymentRecord.requestedRelease?.displayVersion,
    deploymentRecord.gitlab?.jobName,
    deploymentRecord.gitlab?.pipelineId ? `pipeline ${deploymentRecord.gitlab.pipelineId}` : '',
  ];

  return fields.some((field) => String(field || '').toLowerCase().includes(normalizedSearch));
};
